import { useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { Transition } from 'react-transition-group';

import { Link, useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import { CycleMomentsPreview } from 'packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';
import PersonAvatar from 'packages/kakapo-ui-kit/components/users/PersonAvatar';

import TooltipDark from 'packages/kakapo-ui-kit/components/tooltips/TooltipDark';

import InboxManagerHeader from './InboxManagerHeader';
import { InboxManagerTableCell, InboxManagerTableRow } from './InboxManagerTable';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const InboxManagerTableRowContainer = styled(Flex)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlueLight};
`;

const collapseIn = keyframes`
	from {
		opacity: 0;
		transform: scaleY(0.0);
	}

	to {
		opacity: 1;
		transform: none;
	}
`;

const collapseOut = keyframes`
	from {
		opacity: 1;
		transform: none;
	}

	to {
		opacity: 0;
		transform: scaleY(0.0);
	}
`;

const Collapse = styled(Box)<{ entering: boolean; exiting: boolean }>`
	transform-origin: top center;

	${(props) =>
		props.entering
			? css`
					animation: ${collapseIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1);
			  `
			: ``}

	${(props) =>
		props.exiting
			? css`
					animation: ${collapseOut} 0.4s cubic-bezier(0.22, 1, 0.36, 1);
					animation-fill-mode: forwards;
			  `
			: ``}
`;

const Anchor = styled(Link)`
	color: ${(props) => props.theme.colors.primary};

	&:hover {
		text-decoration: underline;

		&:active {
			color: black;
		}
	}
`;

function Clip(props: { text?: string }) {
	const [hover, setHover] = useState(false);

	return (
		<Box
			style={{ position: 'relative' }}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<TooltipDark direction="north" isHovered={hover}>
				{props.text}
			</TooltipDark>
			<Box style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{props.text}</Box>
		</Box>
	);
}

export default function InboxManagerItem(props: {
	index: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	style: any;
	data: {
		dateRange: [Date, Date];
		results: graphql.User[];
		details: Map<string, graphql.User>;
		selected?: string;
		setSelected: (selected: string | undefined) => void;
		setHeight: (height: number) => void;
		header?: boolean;
		sidebar: boolean;
		toggleSidebar: () => void;
	};
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const result = props.data.results[Math.max(0, props.index - 1)];
	const detail = props.data.details.get(result?.id);

	const [visible, setVisible] = useState(false);

	const usesFeedback = useFeature(graphql.FeatureName.FeedbackAdhoc);
	const usesGoals = useFeature(graphql.FeatureName.Goals);
	const usesEvaluations = useFeature(graphql.FeatureName.Evaluations);

	useEffect(() => {
		if (visible && props.data.selected !== result.id) {
			setVisible(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible && props.data.selected !== result.id]);

	if (props.index === 0) {
		return (
			<InboxManagerHeader
				sidebar={props.data.sidebar}
				header={props.data.header}
				toggleSidebar={props.data.toggleSidebar}
			/>
		);
	}

	const jobPosition = detail
		? localize(detail.jobPositions[0]?.jobTitle.title ?? [])?.text
		: undefined;

	return (
		<InboxManagerTableRowContainer
			flexDirection="column"
			style={{
				position: 'absolute',
				top: 0,
				width: '100%',
				height: props.style.height,
				transform: `translateY(${props.style.top}px)`,
				transition: '0.4s cubic-bezier(0.22, 1, 0.36, 1) transform',
			}}
		>
			<InboxManagerTableRow
				pl={[3, 3, 4]}
				clickable
				onClick={() =>
					props.data.selected === result.id
						? props.data.setSelected(undefined)
						: props.data.setSelected(result.id)
				}
			>
				<InboxManagerTableCell small>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					<PersonAvatar size={30 as any} person={detail ?? result} />
				</InboxManagerTableCell>
				<InboxManagerTableCell>
					<Clip text={`${result.firstName} ${result.lastName}`} />
				</InboxManagerTableCell>
				<InboxManagerTableCell>
					{jobPosition ? <Clip text={jobPosition} /> : !detail && <Spinner isLoading size="18px" />}
				</InboxManagerTableCell>
				<InboxManagerTableCell style={{ flexGrow: 1 }}>
					{detail ? (
						<Flex width="100%" flexDirection="column" justifyContent="center" alignItems="stretch">
							<CycleMomentsPreview user={detail} mini dateRange={props.data.dateRange} />
						</Flex>
					) : (
						<Spinner isLoading size="18px" />
					)}
				</InboxManagerTableCell>
			</InboxManagerTableRow>
			{detail && (
				<Transition in={props.data.selected === result.id} timeout={400} mountOnEnter unmountOnExit>
					{(state) => (
						<Collapse entering={state === 'entering'} exiting={state === 'exiting'}>
							<Box
								ref={(ref) => {
									if (!ref) {
										return;
									}

									if (!visible) {
										if (props.data.selected === result.id) {
											props.data.setHeight(ref.offsetHeight);
										}
										setVisible(true);
									}
								}}
							>
								<Box pl={56} pr={3} py={3} style={visible ? {} : { visibility: 'hidden' }}>
									<Flex mb={3}>
										{usesFeedback && (
											<Box mr={2}>
												<Button
													size={ButtonSize.Small}
													onClick={() => history.push(`/user/${result.id}/feedback-v2`)}
												>
													{t('managerInbox.table.goToFeedback')}
												</Button>
											</Box>
										)}
										{usesGoals && (
											<Box mr={2}>
												<Button
													size={ButtonSize.Small}
													onClick={() => history.push(`/user/${result.id}/goals`)}
												>
													{t('managerInbox.table.goToGoals')}
												</Button>
											</Box>
										)}
										{usesEvaluations && (
											<Box mr={2}>
												<Button
													size={ButtonSize.Small}
													onClick={() => history.push(`/user/${result.id}/evaluations-v2`)}
												>
													{t('managerInbox.table.goToEvaluations')}
												</Button>
											</Box>
										)}
									</Flex>
									<CycleMomentsPreview
										user={detail}
										canSkip={false}
										canSendReminder
										dateRange={props.data.dateRange}
									/>
									<Box mt={3}>
										<Anchor to={`/user/${detail.id}`}>
											{t('managerInbox.table.goToProfile', { name: result.firstName })} »
										</Anchor>
									</Box>
								</Box>
							</Box>
						</Collapse>
					)}
				</Transition>
			)}
		</InboxManagerTableRowContainer>
	);
}
